import HelpIcon from '@mui/icons-material/Help';
import { CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import Button from '../../components/atoms/button';
import FileUpload from '../../components/atoms/file-upload';
import InputTextAreaField from '../../components/atoms/input-field/inputTextAreaField';
import InputTextField from '../../components/atoms/input-field/inputTextField';
import Toast, {
  showToasterFailure,
  showToasterSuccess,
} from '../../components/atoms/toast';
import ReassignResource from '../../components/shared/ReassignResource';
import { APIHeader, APIHeaderForUpload } from '../../index';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import { pecrURLData } from '../../reducers/pecr-url-data/slice';
import {
  DepartmentTitle,
  ExecutionEffortCostGrid,
  EstimateLabel,
  FieldsetStyle,
  LegendStyle,
  LocalSiteButtons,
  LocalSiteComment,
  ReviewDoneMessage,
  SiteReviewTitle,
  SiteReviewValue,
  TextAsLink,
  boldFont,
  commentStyle,
  departmentFileUpload,
  homeButtonComponent,
  titleName,
  UploadedDocument,
  uploadedFilesGrid,
  lscDescriptionValue,
  validationErrorOnPages,
  buttonCircularProgress,
} from '../../styles';
import {
  exceptSymbolsInNumberTypeInput,
  getDownloadLink,
  getURL,
  isEmptyObject,
  preventPasteNegative,
  stripTrailingSlash,
  validateInputFields,
} from '../../utils';

const ExecutionDepartment = props => {
  const { apiConfig, userInfo, errorInAPI } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [departmentData, setDepartmentData] = useState({});
  const [displayFiles, setDisplayFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [loader, setLoader] = useState(false);
  const [inputValidationMessage, setInputValidationMessage] = useState('');

  const urlInfo = stripTrailingSlash(window.location.toString()).split('/');
  const eIndex = urlInfo.pop();
  const departmentId = urlInfo.pop();
  const siteId = urlInfo.pop();
  const pecrId = urlInfo.pop();
  const originURL = window.location.origin.toString();
  const url = getURL();

  useEffect(() => {
    if (pecrId && siteId && departmentId && eIndex) {
      dispatch(
        pecrURLData({
          pecrId: pecrId,
          siteId: siteId,
          departmentId: departmentId,
          eIndex: eIndex,
        })
      );
      //Fetch department's data
      fetch(`${apiConfig.uiURL}api/Execution`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          pecrId: pecrId,
          siteId: siteId,
          departmentId: departmentId,
          Index: eIndex,
        }),
      })
        .then(async response => {
          const data = await response.json();
          setDepartmentData(data);
          if (!isEmptyObject(data) && data.ActualStatus === 15) {
            setDisabled(true);
          } else {
            setDisabled(false);
          }

          //Fetch files uploaded on estimate department screen
          fetch(`${apiConfig.uiURL}api/FetchFilesFromDepartment`, {
            method: 'POST',
            headers: APIHeader(),
            body: JSON.stringify({
              MainFolder: 'PECROverview',
              SubFolder: pecrId,
              siteId: siteId,
              departmentId: departmentId,
              index: eIndex,
            }),
          })
            .then(async response => {
              const files = await response.json();
              setDepartmentData(prevState => {
                return {
                  ...prevState,
                  documents: files,
                };
              });
              setLoader(true);
            })
            .catch(() => {
              dispatch(
                errorNotification({
                  hasErrorOccured: true,
                  message: t('error_message'),
                })
              );
            });

          //Fetch files uploaded on execution department screen
          fetch(`${apiConfig.uiURL}api/FetchFilesFromDepartmentExecution`, {
            method: 'POST',
            headers: APIHeader(),
            body: JSON.stringify({
              MainFolder: 'PECROverview',
              SubFolder: pecrId,
              siteId: siteId,
              departmentId: departmentId,
              index: eIndex,
              pecrId: pecrId,
            }),
          })
            .then(async response => {
              const files = await response.json();
              setDisplayFiles(files);
            })
            .catch(() => {
              dispatch(
                errorNotification({
                  hasErrorOccured: true,
                  message: t('error_message'),
                })
              );
            });
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pecrId, siteId, departmentId, eIndex, apiConfig]);

  //To handle the onChange event of all updates
  const handleOnChange = (key, event) => {
    if (key === 'ActualComments') {
      const eventValue = event.target.value.trim();
      setDepartmentData(prevState => {
        return {
          ...prevState,
          [key]: eventValue,
        };
      });
      const ValidationMessage = validateInputFields(eventValue, t);
      setInputValidationMessage(ValidationMessage);
    } else {
      setDepartmentData(prevState => {
        return {
          ...prevState,
          [key]: event.target.value,
        };
      });
    }
  };

  //To handle the deletion of newly uploaded document
  const removeDoc = (event, fileName) => {
    event.preventDefault();
    const remainingFile = displayFiles.filter(file => {
      return file !== fileName;
    });
    setDisplayFiles(remainingFile);
    const remainingUploadFile = uploadedFiles.filter(file => {
      return file.name !== fileName;
    });
    setUploadedFiles(remainingUploadFile);
    fetch(`${apiConfig.uiURL}api/DeleteFile`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        fileName: fileName,
        departmentId: departmentId,
        index: eIndex,
        PID: pecrId,
        folderName: 'Execution',
        email: userInfo.userEmail,
      }),
    }).catch(() => {
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    });
    setErrorMessage('');
  };

  //Function to upload the files
  const fileUpload = () => {
    const uploadData = new FormData();
    for (const file of uploadedFiles) {
      uploadData.append('file', file);
    }
    uploadData.append('MainFolder', 'PECROverview');
    uploadData.append('SubFolder', pecrId);
    uploadData.append('siteId', siteId);
    uploadData.append('departmentId', departmentId);
    uploadData.append('Index', eIndex);
    uploadData.append('NewFolder', pecrId);

    // API call to upload the document
    setShowProgress(true);
    fetch(`${apiConfig.uiURL}api/uploadDocForExecutingDepartment`, {
      headers: APIHeaderForUpload(),
      method: 'POST',
      body: uploadData,
    })
      .then(async res => {
        return res.status === 200 && setShowProgress(false);
      })
      .catch(() => {
        setShowProgress(false);
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To handle the onClick of Job done button
  const onJobDoneClick = () => {
    fetch(`${apiConfig.uiURL}api/UpdateExecution`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        EIndex: eIndex,
        UserId: userInfo.userID,
        EstimateEffort: departmentData.ActualEffort,
        EstimateCost: departmentData.ActualCost,
        Comments: departmentData.ActualComments,
        Label:
          departmentData && departmentData.Label ? departmentData.Label : null,
        Status: 15, // 15	Executed only for job done button
        Currency: departmentData.Currency,
      }),
    })
      .then(async res => {
        //To check the Status of all department before sending mail
        fetch(`${apiConfig.uiURL}api/FetchDepartmentStatus`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({
            pecrId: pecrId,
            siteId: siteId,
          }),
        })
          .then(async response => {
            let shouldSendMail = false;
            const data = await response.json();
            const inProcessDepartments = data.filter(department => {
              return (
                department.ActualStatusName === 'Executing in process' &&
                department.StatusName === 'Estimated'
              );
            });
            if (inProcessDepartments.length === 0) {
              shouldSendMail = true;
            }
            if (shouldSendMail) {
              return (
                //Send Email to Owner
                (
                  fetch(`${apiConfig.uiURL}api/sendEmail`, {
                    headers: APIHeader(),
                    method: 'POST',
                    body: JSON.stringify({
                      toAddress:
                        (departmentData && departmentData.OwnerEmail) || '',
                      fromAddress: 'PECR@carrier.com',
                      Title: (departmentData && departmentData.Title) || '',
                      PecrId: pecrId,
                      loginUserEmail: userInfo.userEmail,
                      type: 'assignEmail',
                      toWhome: 'Owner Job Done',
                      Factory:
                        (departmentData && departmentData.SiteName) || '',
                      creatorsite:
                        (departmentData && departmentData.CreatorSiteName) ||
                        '',
                      creator: `${
                        departmentData && departmentData.CreatorFirstName
                          ? departmentData.CreatorFirstName
                          : ''
                      } ${
                        departmentData && departmentData.CreatorLastName
                          ? departmentData.CreatorLastName
                          : ''
                      }`,
                    }),
                  }).catch(() => {
                    dispatch(
                      errorNotification({
                        hasErrorOccured: true,
                        message: t('error_message'),
                      })
                    );
                  }),
                  //Send Email to Local Coordinator / Global Coordinator
                  fetch(`${apiConfig.uiURL}api/sendEmail`, {
                    headers: APIHeader(),
                    method: 'POST',
                    body: JSON.stringify({
                      toAddress:
                        data[0].SiteDetails === 1
                          ? departmentData.GlobalCoordinatorEmail
                          : departmentData.LSCEmail,
                      fromAddress: 'PECR@carrier.com',
                      Title: (departmentData && departmentData.Title) || '',
                      PecrId: pecrId,
                      loginUserEmail: userInfo.userEmail,
                      type: 'assignEmail',
                      toWhome: 'SC Job Done',
                      URL: `${url}FinalReview/${pecrId}/${siteId}`,
                      creatorsite:
                        (departmentData && departmentData.CreatorSiteName) ||
                        '',
                      creator: `${
                        departmentData && departmentData.CreatorFirstName
                          ? departmentData.CreatorFirstName
                          : ''
                      } ${
                        departmentData && departmentData.CreatorLastName
                          ? departmentData.CreatorLastName
                          : ''
                      }`,
                    }),
                  })
                    .then(res => {
                      console.log(res);
                      fetch(`${apiConfig.uiURL}api/InsertPECRFinalReview`, {
                        method: 'POST',
                        headers: APIHeader(),
                        body: JSON.stringify({
                          pecrId: pecrId,
                          siteId: siteId,
                          StatusId: 15, //Executed
                          ReviewerID:
                            data[0].SiteDetails === 1
                              ? departmentData.GlobalCoordinatorID
                              : departmentData.CoordinatorID,
                        }),
                      }).catch(() => {
                        dispatch(
                          errorNotification({
                            hasErrorOccured: true,
                            message: t('error_message'),
                          })
                        );
                      });
                    })
                    .catch(() => {
                      dispatch(
                        errorNotification({
                          hasErrorOccured: true,
                          message: t('error_message'),
                        })
                      );
                    })
                )
              );
            }
          })
          .catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });
        //To upload the files
        fileUpload();

        //navigate to list page
        navigate('../List');
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To navigate on ExecutionReview page
  const onPECRLinkClick = () => {
    navigate('../ExecutionReview');
  };

  //To navigate on Tracking page in new tab
  const onPECRSummaryLinkClick = () => {
    //navigating to tracking page based on pecrId
    window.open(`${originURL}/TrackingFile?id=${pecrId}`, '_blank');
  };

  //To handle the onClick of Save button
  const onSaveClick = () => {
    setShowProgress(true);
    const updateData = fetch(`${apiConfig.uiURL}api/UpdateExecution`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        EIndex: eIndex,
        UserId: userInfo.userID,
        EstimateEffort: departmentData.ActualEffort,
        EstimateCost: departmentData.ActualCost,
        Comments: departmentData.ActualComments,
        Label:
          departmentData && departmentData.Label ? departmentData.Label : null,
        Currency: departmentData.Currency,
      }),
    })
      .then(async res => {
        //To upload the files
        fileUpload();
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });

    Promise.all([updateData])
      .then(() => {
        showToasterSuccess(t('Save_message'));
      })
      .catch(() => {
        showToasterFailure(t('Save_failed_message'));
      });
  };

  //To download the files on DownloadFile API click
  const onDownloadFileClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFile`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        index: eIndex,
        filename: filename,
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To download the files on DownloadExecutingFile API click
  const onDownloadExecutingFileClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadExecutingFile`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        index: eIndex,
        pecrId: pecrId,
        filename: filename,
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To disable the Job done button
  const disableButton =
    !isEmptyObject(departmentData) &&
    (departmentData.ActualEffort === null ||
      departmentData.ActualCost === null ||
      departmentData.ActualEffort === '' ||
      departmentData.ActualCost === '');

  return errorInAPI.hasErrorOccured ? (
    <ErrorAlert message={errorInAPI.message} />
  ) : !loader || (userInfo && userInfo.userId === '') ? (
    <Grid style={homeButtonComponent}>
      <CircularProgress />
    </Grid>
  ) : userInfo?.active?.toString() === '1' &&
    userInfo?.userId?.toString() !== '0' &&
    Number(userInfo?.userID) === Number(departmentData.EstimatedUserID) &&
    userInfo?.userId !== null ? (
    <Grid>
      {disabled && (
        <Grid style={ReviewDoneMessage}>
          {t('Department_has_been_already_reviewed')}
        </Grid>
      )}
      <Grid style={DepartmentTitle}>
        <Grid>
          <h4 style={titleName}>{`${
            departmentData && departmentData.DepartmentName
              ? departmentData.DepartmentName
              : ''
          } ${t('Execution')}`}</h4>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            gap: '25px',
          }}
        >
          <Grid style={TextAsLink} onClick={onPECRSummaryLinkClick}>
            {t('PECR_Summary_Page')}
          </Grid>
          <Grid style={TextAsLink} onClick={onPECRLinkClick}>
            {t('PECR_Overview')}
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Title')}:</Grid>
          <Grid style={lscDescriptionValue}>
            {`${
              departmentData && departmentData.Title ? departmentData.Title : ''
            }(${pecrId})`}
          </Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Description')}:</Grid>
          <Grid style={lscDescriptionValue}>
            {departmentData && departmentData.Description
              ? departmentData.Description
              : ''}
          </Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Product_Family')}:</Grid>
          <Grid style={SiteReviewValue}>
            {departmentData && departmentData.ProductFamily
              ? departmentData.ProductFamily.split(',').join(', ')
              : ''}
          </Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Creator')}:</Grid>
          <Grid style={SiteReviewValue}>{`${
            departmentData && departmentData.CreatorFirstName
              ? departmentData.CreatorFirstName
              : ''
          } ${
            departmentData && departmentData.CreatorLastName
              ? departmentData.CreatorLastName
              : ''
          } (${
            departmentData && departmentData.CreatorSiteName
              ? departmentData.CreatorSiteName
              : ''
          })`}</Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Owner')}:</Grid>
          <Grid style={SiteReviewValue}>{`${
            departmentData && departmentData.OwnerFirstName
              ? departmentData.OwnerFirstName
              : ''
          } ${
            departmentData && departmentData.OwnerLastName
              ? departmentData.OwnerLastName
              : ''
          }`}</Grid>
        </Grid>
        <Grid style={SiteReviewTitle}>
          <Grid style={boldFont}>{t('Site_Name')}:</Grid>
          <Grid style={SiteReviewValue}>
            {departmentData && departmentData.SiteName
              ? departmentData.SiteName
              : ''}
          </Grid>
        </Grid>
        {departmentData && (
          <ReassignResource
            title={departmentData.Title}
            departmentName={departmentData.DepartmentName}
            updatedBy={departmentData.EstimatedUserID}
            siteCoordinator={departmentData.CoordinatorID}
            pecrId={pecrId}
            siteId={siteId}
            departmentId={departmentId}
            subDepartment={departmentData.Label ?? ''}
          />
        )}

        <Grid style={EstimateLabel} />
        {eIndex !== '0' && (
          <Grid style={SiteReviewTitle}>
            <h4 style={titleName}>{`${
              departmentData && departmentData.Label ? departmentData.Label : ''
            } ${t('Execution')}`}</h4>
            <Tooltip
              title={`${
                departmentData && departmentData.Label
                  ? departmentData.Label
                  : ''
              } ${t('Execution')}`}
              placement="top-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '14px',
                  },
                },
              }}
            >
              <HelpIcon
                sx={{
                  fontSize: 17,
                  position: 'relative',
                  top: '10px',
                  left: '10px',
                }}
              />
            </Tooltip>
          </Grid>
        )}
        <Grid style={ExecutionEffortCostGrid} justifyContent={'space-between'}>
          <fieldset style={FieldsetStyle}>
            <legend style={LegendStyle}> {t('Effort')} </legend>
            <Grid style={ExecutionEffortCostGrid}>
              <Grid style={{ marginRight: '5px' }}>{t('Estimate')}:</Grid>
              <Grid width={110}>
                <InputTextField
                  name={'Effort'}
                  placeholder={t('Effort')}
                  style={{
                    border: '1px solid black',
                    padding: '0px 5px',
                    borderRadius: '5px 0px 0px  5px ',
                  }}
                  value={departmentData.EstimatedEffort}
                  readOnly={true}
                />
              </Grid>
              <Grid
                style={{
                  border: '1px solid black',
                  padding: '0px 7px',
                  borderRadius: '0px 5px 5px  0px ',
                }}
              >
                {t('hrs')}
              </Grid>
            </Grid>
            <Grid style={ExecutionEffortCostGrid}>
              <Grid style={{ marginRight: '5px', marginLeft: '10px' }}>
                *{t('Actual')}:
              </Grid>
              <Grid width={110}>
                <InputTextField
                  name={'Actual_Effort'}
                  placeholder={t('Effort')}
                  style={{
                    border: '1px solid black',
                    padding: '0px 5px',
                    borderRadius: '5px 0px 0px  5px ',
                  }}
                  value={departmentData.ActualEffort}
                  onChange={event => handleOnChange('ActualEffort', event)}
                  type={'number'}
                  disabled={disabled}
                  onKeyDown={e =>
                    exceptSymbolsInNumberTypeInput.includes(e.key) &&
                    e.preventDefault()
                  }
                  onPaste={preventPasteNegative}
                  min="0"
                />
              </Grid>
              <Grid
                style={{
                  border: '1px solid black',
                  padding: '0px 7px',
                  borderRadius: '0px 5px 5px  0px ',
                }}
              >
                {t('hrs')}
              </Grid>
            </Grid>
          </fieldset>
          <fieldset style={FieldsetStyle}>
            <legend style={LegendStyle}> {t('Additional_Costs')} </legend>
            <Grid style={ExecutionEffortCostGrid}>
              <Grid style={{ marginRight: '5px' }}>{t('Estimate')}:</Grid>
              <Grid width={110}>
                <InputTextField
                  name={'AdditionalCost'}
                  placeholder={t('Cost')}
                  style={{
                    border: '1px solid black',
                    padding: '0px 5px',
                    borderRadius: '5px 0px 0px  5px ',
                  }}
                  value={departmentData.EstimatedCost}
                  readOnly={true}
                />
              </Grid>
              <Grid
                style={{
                  border: '1px solid black',
                  padding: '0px 7px',
                  borderRadius: '0px 5px 5px  0px ',
                }}
              >
                {departmentData && departmentData.Currency
                  ? departmentData.Currency
                  : ''}
              </Grid>
            </Grid>
            <Grid style={ExecutionEffortCostGrid}>
              <Grid style={{ marginRight: '5px', marginLeft: '10px' }}>
                *{t('Actual')}:
              </Grid>
              <Grid width={110}>
                <InputTextField
                  name={'AdditionalCost'}
                  placeholder={t('Cost')}
                  style={{
                    border: '1px solid black',
                    padding: '0px 5px',
                    borderRadius: '5px 0px 0px  5px ',
                  }}
                  value={departmentData.ActualCost}
                  onChange={event => handleOnChange('ActualCost', event)}
                  type={'number'}
                  disabled={disabled}
                  onKeyDown={e =>
                    exceptSymbolsInNumberTypeInput.includes(e.key) &&
                    e.preventDefault()
                  }
                  onPaste={preventPasteNegative}
                  min="0"
                />
              </Grid>
              <Grid
                style={{
                  border: '1px solid black',
                  padding: '0px 7px',
                  borderRadius: '0px 5px 5px  0px ',
                }}
              >
                {departmentData && departmentData.Currency
                  ? departmentData.Currency
                  : ''}
              </Grid>
            </Grid>
          </fieldset>
        </Grid>
        <Grid style={LocalSiteComment}>
          <InputTextAreaField
            name={'DepartmentComment'}
            placeholder={!disabled ? t('Add_Comments_here') : ''}
            onChange={event => handleOnChange('ActualComments', event)}
            customStyle={commentStyle}
            value={departmentData.ActualComments}
            disabled={disabled}
          />
        </Grid>
        <Grid style={validationErrorOnPages}>{inputValidationMessage}</Grid>
      </Grid>
      {departmentData &&
      departmentData.documents &&
      departmentData.documents.length > 0 ? (
        <Grid style={SiteReviewTitle}>
          <Grid style={uploadedFilesGrid}>
            <Grid style={UploadedDocument}>
              {disabled
                ? `${t('Earlier_Uploaded_Document')}:`
                : `${t('Uploaded_Document')}:`}
            </Grid>
            <Grid style={UploadedDocument}>
              {departmentData.documents.map((document, index) => {
                return (
                  <Tooltip
                    key={index}
                    title={document}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: '14px',
                        },
                      },
                    }}
                  >
                    <Typography
                      key={index}
                      noWrap
                      width={300}
                      style={TextAsLink}
                      onClick={() => onDownloadFileClick(document)}
                    >
                      {document}
                    </Typography>
                  </Tooltip>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      ) : null}

      {disabled ? (
        displayFiles && displayFiles.length > 0 ? (
          <Grid style={SiteReviewTitle}>
            <Grid style={uploadedFilesGrid}>
              <Grid style={UploadedDocument}>
                {t('Newly_Uploaded_Document')}:
              </Grid>
              <Grid style={UploadedDocument}>
                {displayFiles.map((document, index) => {
                  return (
                    <Tooltip
                      key={index}
                      title={document}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '14px',
                          },
                        },
                      }}
                    >
                      <Typography
                        key={index}
                        noWrap
                        width={300}
                        style={TextAsLink}
                        onClick={() => onDownloadExecutingFileClick(document)}
                      >
                        {document}
                      </Typography>
                    </Tooltip>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        ) : null
      ) : (
        <Grid style={departmentFileUpload}>
          <FileUpload
            displayFiles={displayFiles}
            setDisplayFiles={setDisplayFiles}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            removeDoc={removeDoc}
            disabled={disabled}
          />
        </Grid>
      )}
      <Toast />
      <Grid style={LocalSiteButtons}>
        <Grid sx={{ position: 'relative' }}>
          <Button
            title={t('Save')}
            onClick={onSaveClick}
            disabled={disabled || inputValidationMessage !== '' || showProgress}
          />
          {showProgress && (
            <CircularProgress size={28} sx={buttonCircularProgress} />
          )}
        </Grid>

        <Button
          title={t('Job_done')}
          disabled={disableButton || disabled || inputValidationMessage !== ''}
          onClick={onJobDoneClick}
        />
      </Grid>
    </Grid>
  ) : (
    <Grid style={homeButtonComponent}>{t('No_active_user_message')}</Grid>
  );
};

const mapStateToProps = (state = {}) => {
  return {
    userInfo: state.userInfo,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(ExecutionDepartment);
